import { useCallback } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useMutation } from "@apollo/client/react";
import { notifications } from "@mantine/notifications";
import {
  IconCircleCheck,
  IconCircleX,
  IconShieldCheck,
} from "@tabler/icons-react";
import { VALIDATE_SCRIPT_MUTATION } from "../graphql/mutations";
import { ScriptIssueList } from "./SharedRuleParts";
import {
  ScriptContextType,
  ScriptErrorCode,
  ScriptErrorPhase,
  ScriptWarningCode,
} from "../../../../../../../gql/graphql";

export {
  ScriptContextType,
  ScriptErrorCode,
  ScriptErrorPhase,
  ScriptWarningCode,
};

/**
 * One row of a 5-line excerpt around an error, returned by the simulator for
 * USER_SCRIPT-phase errors. Mirrors `SourceSnippetLineModel` in the schema.
 */
export interface SourceSnippetLine {
  lineNumber: number;
  source: string;
  isErrorLine: boolean;
}

/**
 * Single frame of a Lua execution stack trace, with prelude lines mapped to
 * helper names and user lines mapped to user coordinates. Mirrors
 * `ScriptStackFrameModel` in the schema.
 */
export interface ScriptStackFrame {
  phase: ScriptErrorPhase;
  line: number | null;
  helperName: string | null;
  description: string;
}

export interface ScriptError {
  /**
   * 1-based line in user-script coordinates. May be `null` when the engine
   * could not recover a line (e.g. host-injected helper failures).
   */
  line: number | null;
  column: number;
  message: string;
  code: ScriptErrorCode;
  /** Execution phase. Always set on simulator errors; absent for static-analysis errors. */
  phase?: ScriptErrorPhase | null;
  /** Prelude helper that raised the error, when `phase === PRELUDE`. */
  originHelper?: string | null;
  /** Author-facing remediation hint, when the simulator could match a rule. */
  hint?: string | null;
  /** 5-line source excerpt around the error, for USER_SCRIPT errors. */
  snippet?: ReadonlyArray<SourceSnippetLine> | null;
  /** Multi-frame stack trace; prelude frames carry a `helperName`. */
  stackFrames?: ReadonlyArray<ScriptStackFrame> | null;
}

export interface ScriptWarning {
  line: number;
  column: number;
  message: string;
  code: ScriptWarningCode;
}

interface ValidateScriptInput {
  script: string;
  context: ScriptContextType;
}

interface ValidateScriptResult {
  valid: boolean;
  errors: ScriptError[];
  warnings: ScriptWarning[];
}

interface ValidateScriptResponse {
  validateScript: ValidateScriptResult;
}

interface ValidateScriptVariables {
  input: ValidateScriptInput;
}

interface ValidationPanelProps {
  script: string;
  context: ScriptContextType;
  onValidated?: (result: ValidateScriptResult) => void;
}

export const ValidationPanel = ({
  script,
  context,
  onValidated,
}: ValidationPanelProps) => {
  const [runValidate, { data, loading, reset }] = useMutation<
    ValidateScriptResponse,
    ValidateScriptVariables
  >(VALIDATE_SCRIPT_MUTATION);

  const handleValidate = useCallback(async () => {
    try {
      const response = await runValidate({
        variables: { input: { script, context } },
      });
      if (response.error) {
        throw response.error;
      }
      const result = response.data?.validateScript;
      if (result) {
        onValidated?.(result);
      }
    } catch (err) {
      notifications.show({
        title: "Validation failed",
        message: err instanceof Error ? err.message : "Unknown error",
        color: "red",
      });
    }
  }, [runValidate, script, context, onValidated]);

  const result = data?.validateScript;

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Title order={5}>Script validation</Title>
        <Group gap="xs">
          {loading && <Loader size="xs" />}
          <Button
            size="xs"
            leftSection={<IconShieldCheck size={14} />}
            onClick={() => {
              reset();
              void handleValidate();
            }}
            disabled={loading || script.trim().length === 0}
            loading={loading}
          >
            Validate
          </Button>
        </Group>
      </Group>

      {result && (
        <Stack gap="xs">
          <Group gap="xs">
            <Badge
              color={result.valid ? "green" : "red"}
              size="lg"
              variant="light"
              leftSection={
                result.valid ? (
                  <IconCircleCheck size={12} />
                ) : (
                  <IconCircleX size={12} />
                )
              }
            >
              {result.valid ? "Valid" : "Invalid"}
            </Badge>
            <Text size="xs" c="dimmed">
              {result.errors.length} error(s), {result.warnings.length}{" "}
              warning(s)
            </Text>
          </Group>

          <ScriptIssueList
            issues={result.errors}
            severity="error"
            keyPrefix="validation-error"
          />
          <ScriptIssueList
            issues={result.warnings}
            severity="warning"
            keyPrefix="validation-warning"
          />

          {result.valid &&
            result.errors.length === 0 &&
            result.warnings.length === 0 && (
              <Alert
                icon={<IconShieldCheck size={16} />}
                color="green"
                variant="light"
              >
                Script is valid. No issues detected.
              </Alert>
            )}
        </Stack>
      )}
    </Stack>
  );
};
